import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PropertyDetails } from '../../../../core/models/property-details.model';

type DocumentItem = PropertyDetails['documents'][number];
type TaxItem = PropertyDetails['taxes_and_fees']['items'][number];
type TaxesAndFees = PropertyDetails['taxes_and_fees'];

@Component({
  selector: 'app-documents-taxes',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="grid grid-cols-1 md:grid-cols-2 gap-5 antialiased select-none">
      <div
        class="surface-card p-5 md:p-6 border border-border shadow-sm flex flex-col justify-between"
      >
        <div>
          <div class="flex items-center gap-3 mb-5 pb-3 border-b border-dashed border-border-subtle">
            <div
              class="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center text-primary shadow-sm border border-primary/20"
            >
              <i class="fa-solid fa-folder-open text-xs"></i>
            </div>
            <div>
              <h4 class="text-[10px] font-black uppercase tracking-widest text-text-muted font-mono">
                التخليص القانوني
              </h4>
              <h3 class="text-sm font-black text-text-primary tracking-tight mt-0.5">
                المستندات المتاحة
              </h3>
            </div>
          </div>

          <div class="space-y-2.5">
            @if (documents.length) {
              @for (doc of documents; track doc.name + doc.status) {
                <div
                  class="flex items-center justify-between p-3 bg-bg-secondary/75 border border-border-subtle rounded-2xl transition-all hover:bg-surface-hover hover:border-primary/30 group"
                >
                  <div class="flex items-center gap-3 min-w-0">
                    <div
                      class="w-8 h-8 rounded-xl bg-surface-elevated border border-border flex items-center justify-center text-text-secondary group-hover:text-primary group-hover:border-primary/40 transition-colors shrink-0 shadow-sm"
                    >
                      <i class="fa-solid fa-file-shield text-xs"></i>
                    </div>
                    <span
                      class="text-xs font-bold text-text-primary truncate group-hover:text-primary transition-colors"
                    >
                      {{ valueOrDefault(doc.name, 'ملف مستند قانوني') }}
                    </span>
                  </div>

                  <span
                    class="text-[10px] font-black uppercase tracking-wider font-mono px-2.5 py-1 rounded-lg bg-success/10 text-success border border-success/20 shrink-0"
                  >
                    {{ valueOrDefault(doc.status, 'موثق') }}
                  </span>
                </div>
              }
            } @else {
              <div
                class="py-8 flex flex-col items-center justify-center border border-dashed border-border bg-bg-secondary/40 rounded-2xl text-center font-mono px-3"
              >
                <div
                  class="w-10 h-10 rounded-xl bg-surface-elevated flex items-center justify-center text-text-muted mb-3 border border-border-subtle shadow-sm"
                >
                  <i class="fa-solid fa-files text-sm"></i>
                </div>
                <h4 class="text-[10px] font-black text-text-secondary uppercase tracking-wider mb-1">
                  لا توجد سجلات
                </h4>
                <p
                  class="text-[11px] font-medium text-text-muted max-w-[200px] leading-relaxed normal-case font-sans"
                >
                  لم يتم رفع ملفات قانونية موثقة لهذا الإعلان بعد.
                </p>
              </div>
            }
          </div>
        </div>
      </div>

      <div
        class="surface-card p-5 md:p-6 border border-border shadow-sm flex flex-col justify-between"
      >
        <div>
          <div class="flex items-center gap-3 mb-5 pb-3 border-b border-dashed border-border-subtle">
            <div
              class="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center text-primary shadow-sm border border-primary/20"
            >
              <i class="fa-solid fa-receipt text-xs"></i>
            </div>
            <div>
              <h4 class="text-[10px] font-black uppercase tracking-widest text-text-muted font-mono">
                الحكومة والرسوم
              </h4>
              <h3 class="text-sm font-black text-text-primary tracking-tight mt-0.5">الضرائب والرسوم</h3>
            </div>
          </div>

          <div class="space-y-2.5">
            @if (taxItems.length) {
              @for (tax of taxItems; track tax.name + tax.amount) {
                <div
                  class="p-3 bg-bg-secondary/75 border border-border-subtle rounded-2xl hover:bg-surface-hover hover:border-border transition-colors"
                >
                  <div class="flex items-center justify-between mb-1.5">
                    <span class="text-xs font-bold text-text-primary">{{
                      valueOrDefault(tax.name, 'رسوم إلزامية')
                    }}</span>
                    <span class="text-xs font-black font-mono text-text-primary">{{
                      valueOrDefault(tax.amount, 'غير متوفر')
                    }}</span>
                  </div>
                  <div class="text-[11px] font-medium text-text-muted leading-relaxed">
                    {{ valueOrDefault(tax.note, 'لا توجد تفاصيل إضافية مرفقة.') }}
                  </div>
                </div>
              }
            } @else {
              <div
                class="py-8 flex flex-col items-center justify-center border border-dashed border-border bg-bg-secondary/40 rounded-2xl text-center font-mono px-3"
              >
                <div
                  class="w-10 h-10 rounded-xl bg-surface-elevated flex items-center justify-center text-text-muted mb-3 border border-border-subtle shadow-sm"
                >
                  <i class="fa-solid fa-calculator text-sm"></i>
                </div>
                <h4 class="text-[10px] font-black text-text-secondary uppercase tracking-wider mb-1">
                  معفى / معلق
                </h4>
                <p
                  class="text-[11px] font-medium text-text-muted max-w-[200px] leading-relaxed normal-case font-sans"
                >
                  لم يتم حساب الضرائب والرسوم الهيكلية لهذا العقار.
                </p>
              </div>
            }
          </div>
        </div>

        <div class="pt-5 mt-5 border-t border-dashed border-border-subtle">
          <div class="flex items-center justify-between p-1.5 bg-bg-secondary/50 rounded-xl border border-border-subtle px-3 py-2.5">
            <div class="flex items-center gap-2">
              <span class="text-xs font-black uppercase tracking-wider text-text-primary font-mono"
                >إجمالي المدفوعات المطلوبة</span
              >
              <i
                class="fa-solid fa-circle-info text-[11px] text-text-muted hover:text-primary transition-colors cursor-help"
                title="شاملة جميع البنود المذكورة أعلاه."
              ></i>
            </div>
            <span class="text-sm sm:text-base font-black font-mono text-primary">
              {{ valueOrDefault(taxesAndFees?.total, 'يتم حسابه عند الدفع') }}
            </span>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class DocumentsTaxesComponent {
  @Input() documents: DocumentItem[] = [];
  @Input() taxesAndFees: TaxesAndFees | null = null;

  get taxItems(): TaxItem[] {
    return this.taxesAndFees?.items || [];
  }

  valueOrDefault(value: unknown, fallback: string = 'غير متوفر'): string {
    if (value === null || value === undefined) return fallback;
    const text = String(value).trim();
    return text ? text : fallback;
  }
}
